/**
 * Recent-detections client buffer — a Zustand store of the most-recent
 * SAPIENT DetectionReport rows, the single source feeding BOTH the
 * Detections panel (REQ-UI-061) and (in PR5b) the Map detection-glyph
 * layer. This replaced the Events buffer under the ADR-032 cascade (Event
 * removal, PR6).
 *
 * # Why a store (not per-panel fetches)
 *
 * Per ADR-024 §5 the Map and the Detections panel show the *same*
 * detection set with synchronized selection; the SSE context's own
 * docstring (:file:`map/sse-context.ts`) notes Zustand becomes the right
 * tool "when multiple panes subscribe." Both the panel and (PR5b) the
 * glyph layer subscribe here, so a module-level Zustand store is the
 * smallest tool that scales — no provider boilerplate, narrow selector
 * subscriptions.
 *
 * # Two feeders, one buffer
 *
 *   - **REST back-fill** — :func:`detections/use-recent-detections-feed`
 *     seeds and periodically refreshes the buffer from
 *     ``GET /api/query/recent-detections`` (global, recent window). This
 *     is the authoritative un-filtered list.
 *   - **Live SSE merge** — :func:`detections/use-detections-sse-bridge`
 *     upserts the live ``detection``-kind SSE deltas for the sub-second
 *     in-view latency Flow 02 requires.
 *
 * # Dedup key — ``report_id``, NOT ``object_id``
 *
 * Dedup is by ``report_id`` (the BSI per-message id) so the two feeders
 * converge on the same row. ``object_id`` is the cross-sensor
 * *correlation* key (the same tracked object seen by multiple sensors
 * carries one ``object_id`` across many distinct ``report_id`` reports)
 * and is therefore the wrong dedup key — keying on it would collapse a
 * track's successive detections into a single row.
 *
 * # Retention
 *
 * Detections are pruned on every commit once their detection ``time``
 * ages past :data:`DETECTION_RETENTION_MS` (10 min — mirrors the Events
 * buffer's window per Flow 02 §"Marker conventions"). A safety
 * :data:`DETECTION_BUFFER_CAP` bounds memory under burst; the newest are
 * kept. Idle staleness is bounded by the REST feed's refetch cadence
 * (each refetch upserts and therefore prunes).
 *
 * # The batch path (feature spec ``ui-track-load-headroom`` §2.4)
 *
 * The buffer keeps a **maintained newest-first order** (``order``, report
 * ids) and each row's detection time parsed once at ingest (``timeMs``), so
 * a commit is O(batch) inserts plus an O(pruned) trim from the old end —
 * never a copy-and-re-parse of the whole buffer — and
 * :func:`useRecentDetections` is a map over ``order``, not a sort
 * (REQ-UI-194). The live bridge commits one batch per animation frame
 * through :func:`upsertMany` (REQ-UI-192); :func:`upsert` is the same path
 * for a single batch (the REST feed).
 *
 * Cross-references:
 *   - REQ-UI-027 (Zustand for client state).
 *   - REQ-UI-061 (Detections panel).
 *   - REQ-QUERY-024 / REQ-QUERY-028 (recent + live Detection surfaces).
 *   - REQ-INGEST-044 (nullable position).
 *   - ADR-024 §2 (Detections panel) / §5 (selection contract).
 *   - ADR-032 (Detection supersedes Event).
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md
 *     §"Marker conventions" + §"Empty / no-data states" (the
 *     detection-specific storyboard section lands in PR6; the row
 *     inherits the event-row conventions until then).
 */
import { useMemo } from "react";
import { create } from "zustand";

/**
 * Detections age out 10 min after detection (mirrors the Events buffer's
 * Flow 02 window). The buffer prunes to this window so the panel — and
 * (PR5b) the map — both reflect "the last 10 min". Since ADR-041 this is
 * the **fallback**: the feeders pass the deployment's live window
 * (``UpsertOptions.retentionMs``; REQ-UI-181), of which this equals the
 * BFF's default.
 */
export const DETECTION_RETENTION_MS = 10 * 60 * 1000;

/**
 * Safety bound on buffer size. The global REST feed caps at 100 and the
 * live SSE stream is viewport-scoped (a small in-view rate), so the
 * buffer is normally far below this; the cap only guards against a
 * pathological burst. When exceeded, the newest detections are kept.
 */
export const DETECTION_BUFFER_CAP = 500;

/**
 * The normalized client shape for a SAPIENT detection — the fields the
 * Detections panel rows, the (PR5b) Map glyph layer, and the selection
 * edge need. Both feeders map their wire shape (``SseDetectionPayload`` /
 * ``DetectionItem``) into this; Entity Details fetches the full record by
 * ``report_id`` via the detail endpoint when a detection is selected
 * (PR5a inc 4).
 *
 * **Position is nullable** (REQ-INGEST-044): an unprojectable BSI
 * range/bearing yields ``null`` lat/lng/elev — a *normal* state for a
 * detection, unlike the status feed where a missing position is an error.
 */
export interface DetectionRecord {
  /** BSI report_id ULID — the per-message id; selection-bus id for ``kind: "detection"`` + dedup key. */
  readonly report_id: string;
  /** BSI object_id ULID — the cross-sensor correlation key (NOT the dedup key). */
  readonly object_id: string;
  /**
   * The object track this detection belongs to (ADR-041; REQ-QUERY-053):
   * UUIDv5 over ``(producer_id, object_id)``, BFF-derived and carried by both
   * feeders. Empty string when the BFF supplied none (an older BFF, or an
   * empty key) — such a detection is never hidden as a non-head glyph.
   */
  readonly track_id: string;
  /** Source Producer. */
  readonly producer_id: string;
  /** Detection timestamp, ISO-8601 UTC string. */
  readonly time: string;
  /** WGS84 latitude in decimal degrees; ``null`` when unprojectable. */
  readonly latitude_deg: number | null;
  /** WGS84 longitude in decimal degrees; ``null`` when unprojectable. */
  readonly longitude_deg: number | null;
  /** WGS84 elevation in metres; ``null`` when unprojectable. */
  readonly elevation_m: number | null;
  /** BSI detection state (e.g., ``"CONFIRMED"``); ``null`` when absent. */
  readonly state: string | null;
  /** Associated BSI tasking identifier; ``null`` when untasked. */
  readonly task_id: string | null;
  /** BSI detection confidence in [0, 1]; ``null`` when absent. */
  readonly detection_confidence: number | null;
  /** Primary classification label (the list/filter label); ``null`` when unclassified. */
  readonly primary_class: string | null;
  /**
   * ``time`` parsed to epoch ms, stamped by the store at ingest so ordering,
   * pruning and the map's age fade never re-parse it. Absent only on a record
   * built outside the store (a test literal); consumers fall back to parsing.
   */
  readonly time_ms?: number;
  /** Schema version for the source row. */
  readonly schema_version: number;
  /** Source aggregator identifier; empty string for direct Producers. */
  readonly source_aggregator_id: string;
  /** When the connector accepted the bytes off the wire, ISO-8601 UTC string. */
  readonly ingress_timestamp: string;
  /** SHA-256 of the source payload, lowercase-hex-encoded (64 chars). */
  readonly message_hash: string;
  /** CDS originating-network label when arrived via the Cross Domain Solution; ``null`` when locally-ingested. */
  readonly source_network: string | null;
}

/** Optional knobs for {@link DetectionsState.upsert}. */
export interface UpsertOptions {
  /**
   * The reference "now" (epoch ms) used for age-based pruning. Defaults
   * to ``Date.now()``; tests pass an explicit value for determinism.
   */
  readonly now?: number;
  /**
   * The retention window (ms) — the deployment's live window
   * (``live.window_seconds`` on ``GET /api/config``; ADR-041 D-7 /
   * REQ-UI-181), passed by the feeders. Defaults to
   * :data:`DETECTION_RETENTION_MS` (the BFF's own default) so a caller that
   * has not resolved the config still prunes to the same 10 min.
   */
  readonly retentionMs?: number;
}

/** The detections store shape. */
export interface DetectionsState {
  /** Detections keyed by ``report_id`` (dedup across the two feeders). */
  readonly byId: Record<string, DetectionRecord>;
  /**
   * Every buffered ``report_id``, newest detection time first — the order the
   * Detections panel renders. Maintained on every commit (binary insert),
   * never re-sorted (REQ-UI-194). Consistent with ``byId`` by construction;
   * reset the store with :func:`clear`, not by assigning ``byId`` alone.
   */
  readonly order: readonly string[];
  /** Detection time (epoch ms) per buffered ``report_id``, parsed once at ingest. */
  readonly timeMs: Record<string, number>;
  /**
   * Merge detections into the buffer, then prune by age + cap by size.
   * Last-writer-wins per ``report_id``. One batch, one notification.
   */
  readonly upsert: (detections: readonly DetectionRecord[], opts?: UpsertOptions) => void;
  /**
   * The batch commit the live bridge uses once per frame (REQ-UI-192):
   * identical semantics to :func:`upsert`, named for the call site's intent.
   */
  readonly upsertMany: (detections: readonly DetectionRecord[], opts?: UpsertOptions) => void;
  /** Empty the buffer (e.g., on sign-out / network switch). */
  readonly clear: () => void;
}

/**
 * A detection's time as the order key. An unparseable ``time`` sorts oldest
 * (and is therefore trimmed by age on the next commit) rather than poisoning
 * every comparison with ``NaN``.
 */
function orderTimeMs(time: string): number {
  const parsed = Date.parse(time);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

/**
 * Insert ``id`` into the newest-first ``order`` at the first position whose
 * time is strictly older than ``time`` — equal times keep arrival order.
 */
function insertNewestFirst(
  order: string[],
  timeMs: Readonly<Record<string, number>>,
  id: string,
  time: number,
): void {
  let low = 0;
  let high = order.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if (timeMs[order[mid]] >= time) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  order.splice(low, 0, id);
}

/** The shared commit: merge, order, trim by age from the old end, cap. */
function commitBatch(
  state: DetectionsState,
  detections: readonly DetectionRecord[],
  opts: UpsertOptions | undefined,
): Pick<DetectionsState, "byId" | "order" | "timeMs"> {
  const now = opts?.now ?? Date.now();
  const floor = now - (opts?.retentionMs ?? DETECTION_RETENTION_MS);
  const byId: Record<string, DetectionRecord> = { ...state.byId };
  const timeMs: Record<string, number> = { ...state.timeMs };
  const order = [...state.order];
  for (const detection of detections) {
    const id = detection.report_id;
    const time = orderTimeMs(detection.time);
    const previous = timeMs[id];
    // Stamped on the record as well as in ``timeMs``, so a consumer holding
    // only the row (the map's glyph builder, computing the age fade) never
    // re-parses the timestamp per render — the cost that dominated the
    // 2026-10-07 wedge. Mirrors ``TrackMember.time_ms``.
    byId[id] = detection.time_ms === time ? detection : { ...detection, time_ms: time };
    if (previous === undefined) {
      timeMs[id] = time;
      insertNewestFirst(order, timeMs, id, time);
    } else if (previous !== time) {
      // A re-upsert with a different time (the REST row correcting a live
      // one): move it rather than duplicate it.
      order.splice(order.indexOf(id), 1);
      timeMs[id] = time;
      insertNewestFirst(order, timeMs, id, time);
    }
  }
  // Prune by detection age — the stale rows sit at the old end of the order,
  // so this is O(pruned), not a scan.
  while (order.length > 0 && timeMs[order[order.length - 1]] < floor) {
    const id = order.pop() as string;
    delete byId[id];
    delete timeMs[id];
  }
  // Safety cap: keep the newest DETECTION_BUFFER_CAP.
  while (order.length > DETECTION_BUFFER_CAP) {
    const id = order.pop() as string;
    delete byId[id];
    delete timeMs[id];
  }
  return { byId, order, timeMs };
}

/**
 * The module-level detections store. Consume via the narrow-subscription
 * helpers (:func:`useRecentDetections`, :func:`useDetectionById`) rather
 * than calling as a bare hook. Non-React writers (the SSE bridge effect)
 * use ``useDetectionsStore.getState().upsert(...)``.
 */
export const useDetectionsStore = create<DetectionsState>((set) => ({
  byId: {},
  order: [],
  timeMs: {},
  upsert: (detections, opts) => set((state) => commitBatch(state, detections, opts)),
  upsertMany: (detections, opts) => set((state) => commitBatch(state, detections, opts)),
  clear: () => {
    set({ byId: {}, order: [], timeMs: {} });
  },
}));

// ---- Narrow-subscription helpers ----------------------------------

/**
 * The current buffer as a newest-first array.
 *
 * Subscribes to ``order`` and ``byId`` (stable references between
 * mutations) and maps the maintained order to records via ``useMemo`` keyed
 * on them — a slice of the store's order, never a sort (REQ-UI-194) — so the
 * returned array is referentially stable until the buffer actually changes,
 * avoiding render churn in the Detections panel. An id the order carries but
 * ``byId`` lacks (a test that assigned ``byId`` directly) is skipped.
 */
export function useRecentDetections(): DetectionRecord[] {
  const order = useDetectionsStore((state) => state.order);
  const byId = useDetectionsStore((state) => state.byId);
  return useMemo(() => {
    const records: DetectionRecord[] = [];
    for (const id of order) {
      const record = byId[id];
      if (record !== undefined) {
        records.push(record);
      }
    }
    return records;
  }, [order, byId]);
}

/**
 * The buffered detection with ``reportId``, or ``null`` if absent. Used
 * by the Detections panel for row highlighting and as a cheap cache the
 * (PR5b) Map glyph layer can read; Entity Details fetches the
 * authoritative record by id via the detail endpoint (PR5a inc 4).
 */
export function useDetectionById(reportId: string): DetectionRecord | null {
  return useDetectionsStore((state) => state.byId[reportId] ?? null);
}
